import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListTree, ClipboardCheck, CalendarDays, HelpCircle, Users, FolderHeart, FolderLock, CreditCard, LogOut, Accessibility, MessageSquare } from "lucide-react";
import { Logo } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/app/family", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/app/family/timeline", icon: ListTree, label: "Care Timeline" },
  { to: "/app/family/visits", icon: ClipboardCheck, label: "Visit Reports" },
  { to: "/app/family/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/app/family/requests", icon: HelpCircle, label: "Requests" },
  { to: "/app/family/members", icon: Users, label: "Family Members" },
  { to: "/app/family/care-file", icon: FolderHeart, label: "Care File" },
  { to: "/app/family/documents", icon: FolderLock, label: "Documents", perm: ["full_access"] },
  { to: "/app/family/billing", icon: CreditCard, label: "Plan & Billing", perm: ["full_access", "billing"] },
  { to: "/app/family/communication", icon: MessageSquare, label: "Communication" },
];

export default function FamilyLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-sand" data-testid="family-portal">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/90 backdrop-blur-md">
        <div className="flex h-14 items-center justify-between px-4 sm:px-6">
          <Logo />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/app/senior")} className="hidden items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 hover:border-emerald-700 sm:inline-flex" data-testid="switch-senior-mode">
              <Accessibility className="h-3.5 w-3.5" /> Senior Mode
            </button>
            <span className="hidden text-sm text-stone-500 sm:block">{user?.name}</span>
            <button onClick={() => { logout(); navigate("/"); }} className="rounded-full p-2 text-stone-500 hover:bg-stone-100" aria-label="Log out" data-testid="family-logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-7xl">
        <aside className="hidden w-56 shrink-0 border-r border-stone-200 py-6 pr-2 lg:block">
          <nav className="space-y-1" data-testid="family-side-nav">
            {NAV.filter((n) => !n.perm || n.perm.includes(user?.my_permission || "full_access")).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                data-testid={`family-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-4 pb-24 sm:p-6 lg:pb-6"><Outlet /></main>
      </div>
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex justify-around border-t border-stone-200 bg-white py-2 lg:hidden" data-testid="family-bottom-nav">
        {NAV.filter((n) => !n.perm || n.perm.includes(user?.my_permission || "full_access")).slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-semibold ${isActive ? "text-emerald-900" : "text-stone-400"}`}>
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
